import { Building2 } from 'lucide-react';
import type { JSX } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganizationProgress } from '@/features/memorization/hooks';
import { ProgressStatsGrid } from '@/features/memorization/progress-stats-grid';
import { useHasCapability } from '@/lib/capabilities';

/**
 * Organization-wide progress on the landing page, for the roles that can
 * actually read it.
 *
 * The aggregate reads are gated on `memorization_targets:manage` rather
 * than `:read` (ADR-019's Addendum-to-the-Addendum - a whole-organization
 * roll-up has no per-student row for Self/Child narrowing to apply to), so
 * this renders nothing at all for a Student or Parent instead of firing a
 * request their role would be refused. The numbers themselves come from
 * the materialized summary the backend already maintains (ADR-024) and are
 * rendered through the same `ProgressStatsGrid` every other Progress read
 * uses - no dashboard-only metric invented here.
 */
export function OrganizationProgressCard(): JSX.Element | null {
  const canReadAggregate = useHasCapability('memorization_targets:manage');
  const { data, isLoading, isError } = useOrganizationProgress(canReadAggregate);

  if (!canReadAggregate) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="size-4 text-primary" aria-hidden="true" />
          Progres Organisasi
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && <Skeleton className="h-24 w-full" />}

        {!isLoading && isError && (
          <p className="text-sm text-destructive">Gagal memuat progres organisasi.</p>
        )}

        {!isLoading && !isError && data !== undefined && <ProgressStatsGrid data={data} />}
      </CardContent>
    </Card>
  );
}
